import { useMemo } from 'react';
import { Dayjs } from 'dayjs';
import { PortfolioPeriod } from 'src/components/PortfolioPage/types';
import { PortfolioTradeChartTabValue } from 'src/components/PortfolioPage/PortfolioTradeSubPage/types';
import useAppInitialLoad from 'src/hooks/app/useAppInitialLoad';
import usePortfolioOnChainMetrics from 'src/components/PortfolioPage/PortfolioTradeSubPage/hooks/usePortfolioOnChainMetrics';
import useLatchedValue from 'src/hooks/utils/useLatchedValue';
import usePortfolioTradingSnapshots from 'src/components/PortfolioPage/hooks/usePortfolioTradingSnapshots';
import getPortfolioTradeChartMaxSnapshots from 'src/components/PortfolioPage/PortfolioTradeSubPage/utils/getPortfolioTradeChartMaxSnapshots';
import usePortfolioCumulativePnlChartData from 'src/components/PortfolioPage/PortfolioTradeSubPage/hooks/usePortfolioCumulativePnlChartData';
import usePortfolioIntervalPnlChartData from 'src/components/PortfolioPage/PortfolioTradeSubPage/hooks/usePortfolioIntervalPnlChartData';
import usePortfolioCalendarPnlData from 'src/components/PortfolioPage/PortfolioTradeSubPage/hooks/usePortfolioCalendarPnlData';

interface Params {
	period: PortfolioPeriod;
	includeFeesAndFunding: boolean;
	realizedPnlOnly: boolean;
	tradeChartTab: PortfolioTradeChartTabValue;
	month: Dayjs;
}

export default function usePortfolioTradeChartData({
	period: periodFromProps,
	includeFeesAndFunding,
	realizedPnlOnly,
	tradeChartTab: tradeChartTabFromProps,
	month,
}: Params) {
	const { isInitialLoading } = useAppInitialLoad();

	const maxSnapshots = getPortfolioTradeChartMaxSnapshots({
		period: periodFromProps,
		tradeChartTab: tradeChartTabFromProps,
	});

	const { data, isLoading, ...rest } = usePortfolioTradingSnapshots({
		period: tradeChartTabFromProps === 'calendar' ? '90d' : periodFromProps,
		fillStrategy:
			tradeChartTabFromProps === 'cumulative'
				? 'repeatLastKnown'
				: 'fillWithZero',
		maxSnapshots,
	});

	const onChainMetrics = usePortfolioOnChainMetrics();

	const period = useLatchedValue({
		value: periodFromProps,
		isLatched: rest.isPlaceholderData,
	});

	const { currentCumulativeFunding, currentUnrealizedPnl } = useLatchedValue({
		value: onChainMetrics,
		isLatched: rest.isPlaceholderData,
	});

	const tradeChartTab = useLatchedValue({
		value: tradeChartTabFromProps,
		isLatched: rest.isPlaceholderData,
	});

	const cumulativePnlData = usePortfolioCumulativePnlChartData({
		enabled: tradeChartTab === 'cumulative',
		snapshots: data?.snapshots,
		metrics: data?.metrics,
		currentCumulativeFunding,
		currentUnrealizedPnl,
		includeFeesAndFunding,
		realizedPnlOnly,
	});

	const intervalPnlData = usePortfolioIntervalPnlChartData({
		enabled: tradeChartTab === 'interval',
		period,
		snapshots: data?.snapshots,
		metrics: data?.metrics,
		currentCumulativeFunding,
		currentUnrealizedPnl,
		includeFeesAndFunding,
		realizedPnlOnly,
	});

	const calendarPnlData = usePortfolioCalendarPnlData({
		enabled: tradeChartTab === 'calendar',
		snapshots: data?.snapshots,
		metrics: data?.metrics,
		currentCumulativeFunding,
		currentUnrealizedPnl,
		includeFeesAndFunding,
		realizedPnlOnly,
		month,
	});

	const processedData = useMemo(() => {
		return {
			cumulativePnlData,
			intervalPnlData,
			calendarPnlData,
		};
	}, [cumulativePnlData, intervalPnlData, calendarPnlData]);

	return {
		data: processedData,
		isLoading: isLoading || isInitialLoading,
		...rest,
	};
}
